import { z } from 'zod';

// ─── Password Policy ────────────────────────────────────────────────

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

/** Basic password — used for login (no strength requirements). */
export const PasswordSchema = z
	.string()
	.min(1, 'Password is required')
	.max(PASSWORD_MAX, `Password must be at most ${PASSWORD_MAX} characters`);

/** Strong password — used for signup and reset (enforces strength rules). */
export const StrongPasswordSchema = z
	.string()
	.min(PASSWORD_MIN, `Password must be at least ${PASSWORD_MIN} characters`)
	.max(PASSWORD_MAX, `Password must be at most ${PASSWORD_MAX} characters`)
	.regex(/[a-z]/, 'Password must contain a lowercase letter')
	.regex(/[A-Z]/, 'Password must contain an uppercase letter')
	.regex(/[0-9]/, 'Password must contain a number')
	.regex(/[^a-zA-Z0-9]/, 'Password must contain a special character');

export interface PasswordStrengthResult {
	score: 0 | 1 | 2 | 3 | 4;
	label: 'Too short' | 'Weak' | 'Fair' | 'Good' | 'Strong';
	checks: {
		length: boolean;
		lowercase: boolean;
		uppercase: boolean;
		number: boolean;
		special: boolean;
	};
}

/** Pure function — safe on both client and server. */
export function passwordStrength(password: string): PasswordStrengthResult {
	const checks = {
		length: password.length >= PASSWORD_MIN,
		lowercase: /[a-z]/.test(password),
		uppercase: /[A-Z]/.test(password),
		number: /[0-9]/.test(password),
		special: /[^a-zA-Z0-9]/.test(password),
	};

	const passed = Object.values(checks).filter(Boolean).length;
	const score = Math.min(passed, 4) as 0 | 1 | 2 | 3 | 4;
	const labels: Record<number, PasswordStrengthResult['label']> = {
		0: 'Too short',
		1: 'Weak',
		2: 'Fair',
		3: 'Good',
		4: 'Strong',
	};

	return { score, label: labels[score], checks };
}

// ─── Auth Form Schemas ──────────────────────────────────────────────

export const LoginInputSchema = z.object({
	email: z.string().email('Enter a valid email address'),
	password: PasswordSchema,
});

export const SignupInputSchema = z.object({
	name: z.string().min(1, 'Name is required'),
	email: z.string().email('Enter a valid email address'),
	password: StrongPasswordSchema,
});

export const ForgotPasswordInputSchema = z.object({
	email: z.string().email('Enter a valid email address'),
});

export const ResetPasswordInputSchema = z
	.object({
		password: StrongPasswordSchema,
		confirmPassword: z.string().min(1, 'Please confirm your password'),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Passwords do not match',
		path: ['confirmPassword'],
	});

export const ChangePasswordInputSchema = z
	.object({
		currentPassword: z.string().min(1, 'Current password is required'),
		newPassword: StrongPasswordSchema,
		confirmNewPassword: z.string().min(1, 'Please confirm your new password'),
	})
	.refine((data) => data.newPassword === data.confirmNewPassword, {
		message: 'Passwords do not match',
		path: ['confirmNewPassword'],
	});

export const TotpVerifyInputSchema = z.object({
	code: z
		.string()
		.length(6, 'Code must be 6 digits')
		.regex(/^\d{6}$/, 'Code must be 6 digits'),
});

// ─── Types ──────────────────────────────────────────────────────────
export type LoginInput = z.infer<typeof LoginInputSchema>;
export type SignupInput = z.infer<typeof SignupInputSchema>;
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInputSchema>;
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;
export type ChangePasswordInput = z.infer<typeof ChangePasswordInputSchema>;
export type TotpVerifyInput = z.infer<typeof TotpVerifyInputSchema>;
